/**
 * Classifies an error caught by the orchestrator's apply pipeline into one of
 * three normalized symptom strings, used by `fireToolCallApplyFailed` to drive
 * the shared `attributeError` routing table.
 *
 * The classification is intentionally conservative: definitive ProseMirror
 * `ReplaceError` instances and message-pattern-matched ADF schema validation
 * errors are routed to specific symptoms; anything ambiguous falls back to the
 * generic `apply_failed`, which routes to `unknown`/`low` so dashboards do not
 * incorrectly attribute fault to either layer.
 *
 * Pure function — no side effects, no logging, no external dependencies.
 */

export type ApplyErrorSymptom =
	| 'apply_failed_pm_error'
	| 'apply_failed_adf_schema_error'
	| 'apply_failed';

/**
 * Patterns thrown by `prosemirror-model`'s `Node.fromJSON` /
 * `Mark.fromJSON` / `Fragment.fromJSON` when the incoming ADF describes a
 * node-type, mark-type, content-type, or shape that the local schema cannot
 * accept. These are caused by the *incoming* model output, not by the
 * orchestrator's apply logic — so they route to backend-quality attribution.
 *
 * See prosemirror-model/dist/index.cjs throw-sites for the canonical list.
 */
/* eslint-disable require-unicode-regexp -- The orchestrator's tsconfig
   targets ES5, which does not support the `u` flag in regex literals
   (TS1501). None of these patterns use Unicode-specific features, so
   the flag is not needed at runtime. */
const ADF_SCHEMA_ERROR_PATTERNS: readonly RegExp[] = [
	/^Invalid content for node type/,
	/^There is no (?:node|mark) type/,
	/^Invalid mark data for /,
	/^Empty text nodes are not allowed/,
	/^Invalid input for (?:Mark|Node|Fragment|Slice)\.fromJSON/,
	/^Invalid collection of marks for node /,
	/^Invalid text node in JSON/,
];

/**
 * `RangeError` messages emitted by `prosemirror-model` and
 * `prosemirror-transform` from transaction-mechanics throw sites
 * (positions, replace/wrap/lift/join validation, slice resolution).
 *
 * Anything matching this list is a frontend apply-logic concern.
 *
 * Anything that is a `RangeError` but does NOT match either this list
 * OR the ADF schema list above is treated as ambiguous — native JS
 * also throws `RangeError` for non-PM reasons (e.g. "Maximum call
 * stack size exceeded", "Invalid array length", `Intl` constructor
 * failures, third-party libraries) and we deliberately route those
 * to `apply_failed` (`unknown`/`low`) so dashboards do not
 * misattribute them to either layer.
 */
const PM_TRANSACTION_ERROR_PATTERNS: readonly RegExp[] = [
	/^Position [0-9]+ out of range/,
	/^Inserted content deeper than insertion position/,
	/^Cannot (?:insert|replace|delete|wrap|join|split|lift|unwrap)/i,
	/^Selected node can't be replaced by content/,
	/can't (?:join|wrap|fit|lift|split)/i,
	/^Removing non-flat range/,
	/^Cannot find a valid cursor position/,
	/^Wrap (?:depth|range) /,
	/^Lifted range/,
	/^Tried to (?:lift|wrap|join|step)/,
	/^Step result has /,
	/^Invalid (?:position|step|content) for /,
];
/* eslint-enable require-unicode-regexp */

/**
 * Returns the apply-layer symptom for an error caught by `processQueue`.
 *
 * Classification:
 * - `apply_failed_pm_error`        — ProseMirror `ReplaceError` (defined in
 *                                   prosemirror-model). Signals a transaction
 *                                   that violated schema invariants
 *                                   (open-depth mismatch, content-type
 *                                   mismatch, node-join incompatibility).
 *                                   Frontend apply logic owns these.
 * - `apply_failed_adf_schema_error` — `RangeError` whose message matches an
 *                                   ADF schema validation pattern (see
 *                                   `ADF_SCHEMA_ERROR_PATTERNS`). The
 *                                   incoming ADF references a node/mark type
 *                                   not in the local schema, or has invalid
 *                                   shape. Backend generation owns these.
 * - `apply_failed`                  — anything else (plain `Error`, untyped
 *                                   throws, RangeErrors with messages outside
 *                                   the schema-validation set). Routes to
 *                                   `unknown`/`low` so we do not guess.
 */
export function classifyApplyError(error: unknown): {
	symptom: ApplyErrorSymptom;
} {
	if (!(error instanceof Error)) {
		return { symptom: 'apply_failed' };
	}

	// `ReplaceError` is exported by prosemirror-model and extends `Error`.
	// We match by `.name === 'ReplaceError'` rather than `instanceof` to
	// avoid a runtime dependency on prosemirror-model and to remain robust
	// across multiple loaded copies of the module.
	if (error.name === 'ReplaceError') {
		return { symptom: 'apply_failed_pm_error' };
	}

	if (error instanceof RangeError) {
		for (const pattern of ADF_SCHEMA_ERROR_PATTERNS) {
			if (pattern.test(error.message)) {
				return { symptom: 'apply_failed_adf_schema_error' };
			}
		}
		for (const pattern of PM_TRANSACTION_ERROR_PATTERNS) {
			if (pattern.test(error.message)) {
				return { symptom: 'apply_failed_pm_error' };
			}
		}
		// Unmatched `RangeError` — could be a JS-engine error (e.g.
		// "Maximum call stack size exceeded"), an `Intl`/Date/Number
		// validation throw, or a third-party library throw. Route to
		// `apply_failed` so dashboards stay conservative and do not
		// misattribute non-PM faults to the apply layer.
	}

	return { symptom: 'apply_failed' };
}
